// React hooks for state, side effects, and references
import { useState, useEffect, useRef } from 'react';
// React Router hook for navigation
import { useNavigate } from 'react-router-dom';
// Custom authentication hook
import { useAuth } from '../hooks/useAuth';
// Custom hook to fetch home page data
import { useHomeData } from '../hooks/useHomeData';
// Home page UI components
import Navbar from '../components/common/home/Navbar';
import Sidebar from '../components/common/home/Sidebar';
import HeroSection from '../components/common/home/HeroSection';
import CourseSection from '../components/common/home/CourseSection';
import DeadlinesSection from '../components/common/home/DeadlinesSection';
import StatusUpdatesSection from '../components/common/home/StatusUpdatesSection';
import Footer from '../components/common/home/Footer';
// Axios instance for HTTP requests
import axiosClient from '../api/axiosClient';
// UI icons for expand/collapse
import { ChevronDown, ChevronUp } from 'lucide-react';
// Type definition for Course
import type {Course} from "../types";

function Home() {
  // Authentication state and logout function
  const { user, isLoading, logout } = useAuth();
  // Home page data: courses, updates, deadlines, notifications, errors, and setters
  const {
    enrolledCourses,
    availableCourses,
    updates,
    deadlines,
    notifications,
    error,
    setNotifications,
    setEnrolledCourses,
    setAvailableCourses,
  } = useHomeData(user, isLoading);
  // Local UI state for sidebar, user menu, notifications dropdown, error messages, collapsible sections
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [displayedError, setDisplayedError] = useState<string | null>(null);
  const [isEnrolledCoursesOpen, setIsEnrolledCoursesOpen] = useState(true);
  const [isAvailableCoursesOpen, setIsAvailableCoursesOpen] = useState(true);
  const [isDeadlinesOpen, setIsDeadlinesOpen] = useState(true);
  const [isUpdatesOpen, setIsUpdatesOpen] = useState(true);
  // Navigation hook
  const navigate = useNavigate();
  // Refs for user menu and notifications dropdown (for click outside handling)
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  // Boolean for teacher role
  const isTeacher = user?.role === 'teacher';
  // Dynamic title depending on role
  const enrolledCoursesTitle = isTeacher ? 'Your Courses' : 'Your Enrolled Courses';
  // Function to refresh courses from backend
  const refreshCourses = async () => {
    try {
      // Fetch all courses
      const coursesResponse = await axiosClient.get('/elearning/api/courses/', {
        headers: {
          'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
        },
      });
      const coursesData = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];
      let enrolledCourseIds: number[] = [];
      let enrolledCoursesData: Course[] = [];
      // If user is a student, determine enrolled and available courses
      if (user?.role === 'student') {
        try {
          // Fetch student enrollments
          const enrollmentsResponse = await axiosClient.get('/elearning/api/enrollments/', {
            headers: {
              'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
            },
          });
          if (Array.isArray(enrollmentsResponse.data)) {
            // Map enrollments to course objects
            enrolledCoursesData = enrollmentsResponse.data
              .filter((e: any) => e.course && (typeof e.course.id === 'number' || typeof e.course.id === 'string'))
              .map((e: any) => ({
                ...e.course,
                id: Number(e.course.id),
                enrolled: true,
                slug: e.course.slug || `course-${e.course.id}`,
                teacher: {
                  ...e.course.teacher,
                  id: Number(e.course.teacher.id),
                  email: e.course.teacher.email || null,
                  real_name: e.course.teacher.real_name || null,
                  photo: e.course.teacher.photo || null,
                },
              }));
            enrolledCourseIds = enrolledCoursesData.map((course: any) => course.id);
          } else {
            console.warn('[Home] Enrollments response is not an array:', enrollmentsResponse.data);
          }
        } catch (enrollErr: any) {
          console.error('[Home] Enrollments error:', JSON.stringify(enrollErr.response?.data || enrollErr.message, null, 2));
          enrolledCoursesData = [];
          enrolledCourseIds = [];
        }
        // Update state with enrolled courses
        setEnrolledCourses(enrolledCoursesData);
        // Determine available courses (not enrolled)
        const available = coursesData
          .filter((course: any) => {
            if (!course || !course.id || !course.teacher) {
              console.warn('[Home] Invalid course data:', course);
              return false;
            }
            const courseId = Number(course.id);
            const isEnrolled = enrolledCourseIds.includes(courseId);
            return !isEnrolled;
          })
          .map((course: any) => ({
            ...course,
            id: Number(course.id),
            enrolled: false,
            slug: course.slug || `course-${course.id}`,
            teacher: {
              ...course.teacher,
              id: Number(course.teacher.id),
              email: course.teacher.email || null,
              real_name: course.teacher.real_name || null,
              photo: course.teacher.photo || null,
            },
          }));
        setAvailableCourses(available);
      } else if (user?.role === 'teacher') {
        // For teachers, show only courses they teach
        setEnrolledCourses(coursesData
          .filter((course: any) => course.teacher && Number(course.teacher.id) === user.id)
          .map((course: any) => ({
            ...course,
            id: Number(course.id),
            enrolled: false,
            slug: course.slug || `course-${course.id}`,
            teacher: {
              ...course.teacher,
              id: Number(course.teacher.id),
              email: course.teacher.email || null,
              real_name: course.teacher.real_name || null,
              photo: course.teacher.photo || null,
            },
          })));
        setAvailableCourses([]);
      }
      // Clear any displayed errors
      setDisplayedError(null);
    } catch (err: any) {
      // Handle errors during course refresh
      const errorMessage = err.response?.data?.error || err.message || 'Failed to refresh courses';
      console.error('[Home] Error refreshing courses:', JSON.stringify(err.response?.data || err.message, null, 2));
      setDisplayedError(errorMessage);
    }
  };
  // Redirect to login if no authenticated user
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    }
  }, [isLoading, user, navigate]);
  // Automatically clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      setDisplayedError(error);
      const timer = setTimeout(() => setDisplayedError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  // Mark a notification as read
  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await axiosClient.patch(`/elearning/api/notifications/${notificationId}/`, { is_read: true });
      // Update notifications in state
      setNotifications(notifications.map(notif =>
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      ));
    } catch (err: any) {
      console.error('[Home] Error marking notification as read:', JSON.stringify(err.response?.data || err.message, null, 2));
      setDisplayedError(err.response?.data?.error || 'Failed to mark notification as read');
    }
  };
  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err: any) {
      console.error('[Home] Error logging out:', JSON.stringify(err.response?.data || err.message, null, 2));
      setDisplayedError(err.message || 'Logout failed');
    }
  };
  // Show loading spinner while authentication is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Return null if user is not authenticated
  if (!user) {
    return null;
  }
  // Main JSX layout of the Home page
return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 font-sans text-gray-900">
    {/* Navbar with user info, notifications, menus, and logout */}
    <Navbar
      user={user}
      notifications={notifications}
      isUserMenuOpen={isUserMenuOpen}
      setIsUserMenuOpen={setIsUserMenuOpen}
      isNotificationsOpen={isNotificationsOpen}
      setIsNotificationsOpen={setIsNotificationsOpen}
      isSidebarOpen={isSidebarOpen}
      setIsSidebarOpen={setIsSidebarOpen}
      userMenuRef={userMenuRef}
      notificationsRef={notificationsRef}
      handleLogout={handleLogout}
      onMarkAsRead={handleMarkAsRead}
    />

    {/* Sidebar menu */}
    <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

    {/* Hero section with welcome info */}
    <HeroSection user={user} />

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Error display */}
      {displayedError && (
        <div className="mb-8 flex items-center gap-2 p-4 rounded-xl bg-red-100 border border-red-300 text-red-800 shadow-md animate-pulse">
          <svg
            className="h-5 w-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{displayedError}</span>
        </div>
      )}

      {/* Enrolled courses section */}
      <div className="mb-10">
        <div className="flex justify-between items-center bg-white/70 backdrop-blur-md border border-gray-200 p-5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
          <h2 className="text-2xl font-semibold tracking-tight">{enrolledCoursesTitle}</h2>
          <button
            onClick={() => setIsEnrolledCoursesOpen(!isEnrolledCoursesOpen)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            {isEnrolledCoursesOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>
        {isEnrolledCoursesOpen && (
          <div className="mt-4 animate-fadeIn">
            <CourseSection
              title={enrolledCoursesTitle}
              courses={enrolledCourses}
              emptyMessage={`No ${enrolledCoursesTitle.toLowerCase()}.`}
              user={user}
              refreshCourses={refreshCourses}
            />
          </div>
        )}
      </div>

      {/* Available courses section (only for students) */}
      {!isTeacher && (
        <div className="mb-10">
          <div className="flex justify-between items-center bg-white/70 backdrop-blur-md border border-gray-200 p-5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
            <h2 className="text-2xl font-semibold tracking-tight">Explore More Courses</h2>
            <button
              onClick={() => setIsAvailableCoursesOpen(!isAvailableCoursesOpen)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              {isAvailableCoursesOpen ? (
                <ChevronUp className="w-6 h-6 text-gray-600" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
          {isAvailableCoursesOpen && (
            <div className="mt-4 animate-fadeIn">
              <CourseSection
                title="Explore More Courses"
                courses={availableCourses}
                emptyMessage="No available courses."
                user={user}
                refreshCourses={refreshCourses}
              />
            </div>
          )}
        </div>
      )}

      {/* Deadlines section */}
      <div className="mb-10">
        <div className="flex justify-between items-center bg-white/70 backdrop-blur-md border border-gray-200 p-5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
          <h2 className="text-2xl font-semibold tracking-tight">Upcoming Deadlines</h2>
          <button
            onClick={() => setIsDeadlinesOpen(!isDeadlinesOpen)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            {isDeadlinesOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>
        {isDeadlinesOpen && (
          <div className="mt-4 animate-fadeIn">
            <DeadlinesSection deadlines={deadlines} />
          </div>
        )}
      </div>

      {/* Status updates section */}
      <div className="mb-10">
        <div className="flex justify-between items-center bg-white/70 backdrop-blur-md border border-gray-200 p-5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
          <h2 className="text-2xl font-semibold tracking-tight">Recent Updates</h2>
          <button
            onClick={() => setIsUpdatesOpen(!isUpdatesOpen)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            {isUpdatesOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>
        {isUpdatesOpen && (
          <div className="mt-4 animate-fadeIn">
            <StatusUpdatesSection updates={updates} />
          </div>
        )}
      </div>
    </div>

    {/* Footer */}
    <Footer />
  </div>
);

}
export default Home;